import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import analysisService, {
  FullAnalysisResult,
} from '../services/analysisService';

const InterviewResultPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resultId } = useParams<{ resultId: string }>();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<FullAnalysisResult | null>(null);
  const [activeTab, setActiveTab] = useState<'problem' | 'analysis'>(
    'problem' // Default to problem tab, will be updated based on analysis status
  );

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      return;
    }

    if (!user) {
      navigate('/');
      return;
    }

    const loadAnalysisResult = async () => {
      if (resultId) {
        // Fetch full analysis using result ID
        try {
          setLoading(true);
          const result = await analysisService.getAnalysisResult(resultId);
          setAnalysisResult(result);
        } catch (error) {
          console.error('Failed to load analysis result:', error);
          setError(
            error instanceof Error ? error.message : 'Failed to load analysis'
          );
        } finally {
          setLoading(false);
        }
      } else {
        // No result ID provided - redirect to practice page
        setLoading(false);
        setError('No analysis result ID provided');
      }
    };

    loadAnalysisResult();
  }, [user, authLoading, navigate, resultId, location.state]);

  // Set correct default tab based on analysis status
  useEffect(() => {
    if (analysisResult) {
      if (analysisResult.status === 'completed') {
        setActiveTab('analysis'); // Show analysis tab for completed results
      } else {
        setActiveTab('problem'); // Default to problem tab for pending/failed results
      }
    }
  }, [analysisResult]);

  if (authLoading || loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-gray-600'>
          {authLoading ? 'Loading...' : 'Loading results...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='max-w-md w-full mx-4 text-center'>
          <div className='text-red-600 text-lg mb-4'>Error loading results</div>
          <div className='text-gray-600 mb-6'>{error}</div>
          <div className='space-x-4'>
            <button
              onClick={() => window.location.reload()}
              className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/practice')}
              className='px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300'
            >
              Back to Practice
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use analysis result if available, otherwise fallback to default data
  const sessionData = analysisResult?.sessionMetadata;

  const performanceData = analysisResult?.performanceAnalysis;

  const solutionData = analysisResult?.optimalSolution;

  const improvementsData = analysisResult?.detailedFeedback?.improvements;

  const transcript =
    analysisResult?.transcriptData?.fullTranscript || 'No transcript available';

  if (!sessionData || !performanceData || !solutionData || !improvementsData) {
    if (loading) {
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
          <div className='text-gray-600'>Loading results...</div>
        </div>
      );
    }
    if (error) {
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
          <div className='max-w-md w-full mx-4 text-center'>
            <div className='text-red-600 text-lg mb-4'>
              Error loading results
            </div>
            <div className='text-gray-600 mb-6'>{error}</div>
            <div className='space-x-4'>
              <button
                onClick={() => window.location.reload()}
                className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/practice')}
                className='px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300'
              >
                Back to Practice
              </button>
            </div>
          </div>
        </div>
      );
    }
    // If no loading and no error, but missing data - redirect to practice
    navigate('/practice');
    return null;
  }

  return (
    <div className='overflow-y-auto h-[100vh] bg-gray-50 py-8'>
      <div className='max-w-6xl mx-auto px-4 mb-16'>
        {/* Header */}
        <div className='text-center mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            Interview Analysis
          </h1>
          <p className='text-gray-600'>
            Here's your detailed performance analysis and feedback
          </p>
        </div>

        {/* Failed Analysis Warning */}
        {analysisResult?.status === 'failed' && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-start'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-red-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-red-800'>
                  Analysis Failed
                </h3>
                <div className='mt-2 text-sm text-red-700'>
                  <p>
                    We encountered an issue analyzing your interview. Please
                    contact our support team and we'll help resolve this for
                    you. You can still review your problem and solution below.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Analysis Warning */}
        {analysisResult?.status !== 'completed' &&
          analysisResult?.status !== 'failed' && (
            <div className='bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6'>
              <div className='flex items-start'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-orange-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-orange-800'>
                    Analysis In Progress
                  </h3>
                  <div className='mt-2 text-sm text-orange-700'>
                    <p>
                      Your interview analysis is still being processed. The
                      detailed performance analysis and feedback will be
                      available in 2-3 minutes. You can view your problem and
                      solution below while you wait.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Insufficient Data Warning */}
        {analysisResult?.insufficientData && (
          <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6'>
            <div className='flex items-start'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-yellow-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-yellow-800'>
                  Limited Analysis Data
                </h3>
                <div className='mt-2 text-sm text-yellow-700'>
                  <p>
                    Some analysis results may be less reliable due to limited
                    data. This can happen with shorter interviews or minimal
                    code changes.
                  </p>
                  {/* {analysisResult?.dataCoverage && (
                    <div className='mt-3 text-xs'>
                      <div className='grid grid-cols-3 gap-4'>
                        <div>
                          <span className='font-medium'>Transcript:</span>{' '}
                          {analysisResult.dataCoverage.transcriptChars} chars
                        </div>
                        <div>
                          <span className='font-medium'>Final Code:</span>{' '}
                          {analysisResult.dataCoverage.finalCodeChars} chars
                        </div>
                        <div>
                          <span className='font-medium'>Code Changes:</span>{' '}
                          {analysisResult.dataCoverage.codeHistoryCount} edits
                        </div>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session Info */}
        <div className='bg-white rounded-lg shadow-lg p-6 mb-8'>
          <div className='flex justify-between items-center'>
            <div>
              <h2 className='text-xl font-semibold text-gray-900'>
                {sessionData.problemTitle} ({sessionData.difficulty})
              </h2>
              <p className='text-gray-600'>
                Session Duration: {sessionData.sessionTime}
              </p>
            </div>
            <div className='text-right'>
              {analysisResult?.status === 'completed' ? (
                <>
                  <div className='text-2xl font-bold text-blue-600'>
                    {performanceData.overallScore}/100
                  </div>
                  <div className='text-sm text-gray-500'>Overall Score</div>
                </>
              ) : analysisResult?.status === 'failed' ? (
                <>
                  <div className='text-2xl font-bold text-red-600'>Failed</div>
                  <div className='text-sm text-gray-500'>Status</div>
                </>
              ) : (
                <>
                  <div className='text-2xl font-bold text-orange-600'>
                    Pending
                  </div>
                  <div className='text-sm text-gray-500'>Status</div>
                </>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className='mt-6 border-t pt-6'>
            <div className='flex space-x-1 rounded-lg bg-gray-100 p-1'>
              <button
                onClick={() => setActiveTab('problem')}
                className={`flex-1 rounded-md py-2 px-4 text-sm font-medium transition-colors ${
                  activeTab === 'problem'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Problem & Solution
              </button>
              <button
                onClick={() =>
                  analysisResult?.status === 'completed' &&
                  setActiveTab('analysis')
                }
                disabled={analysisResult?.status !== 'completed'}
                className={`flex-1 rounded-md py-2 px-4 text-sm font-medium transition-colors ${
                  analysisResult?.status !== 'completed'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : activeTab === 'analysis'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Analysis & Feedback
                {analysisResult?.status !== 'completed' &&
                  analysisResult?.status !== 'failed' && (
                    <span className='ml-1 text-xs'>(Pending)</span>
                  )}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'problem' && (
          <div className='space-y-8'>
            {/* Problem Statement and User's Final Code - Side by Side */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
              {/* Problem Statement */}
              <div className='bg-white rounded-lg shadow-lg p-6'>
                <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                  Problem Statement
                </h3>
                <div className='prose max-w-none'>
                  <p className='text-gray-700 mb-4'>
                    {sessionData.problemDescription}
                  </p>
                  {sessionData.problemExamples &&
                    sessionData.problemExamples.length > 0 && (
                      <div>
                        <h4 className='font-semibold text-gray-900 mb-2'>
                          Examples:
                        </h4>
                        {sessionData.problemExamples.map((example, index) => (
                          <div
                            key={index}
                            className='mb-3 p-3 bg-gray-50 rounded-lg'
                          >
                            <div className='text-sm'>
                              <div>
                                <strong>Input:</strong> {example.input}
                              </div>
                              <div>
                                <strong>Output:</strong> {example.output}
                              </div>
                              {example.explanation && (
                                <div>
                                  <strong>Explanation:</strong>{' '}
                                  {example.explanation}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  {sessionData.problemConstraints &&
                    sessionData.problemConstraints.length > 0 && (
                      <div className='mb-4'>
                        <h4 className='font-semibold text-gray-900 mb-2'>
                          Constraints:
                        </h4>
                        <ul className='list-disc list-inside text-gray-700'>
                          {sessionData.problemConstraints.map(
                            (constraint, index) => (
                              <li key={index}>{constraint}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </div>

              {/* User's Final Code */}
              <div className='bg-white rounded-lg shadow-lg p-6'>
                <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                  Your Final Solution
                </h3>
                <div className='bg-gray-900 rounded-lg p-4 overflow-x-auto'>
                  <pre className='text-sm text-gray-100'>
                    <code>
                      {analysisResult?.codeProgression?.finalCode ||
                        'No solution provided'}
                    </code>
                  </pre>
                </div>
                <div className='mt-4 text-sm text-gray-600'>
                  Language:{' '}
                  <span className='font-medium'>
                    {analysisResult?.codeProgression?.language ||
                      sessionData.language}
                  </span>
                </div>
              </div>
            </div>

            {/* Transcript */}
            <div className='bg-white rounded-lg shadow-lg p-6'>
              <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                Interview Transcript
              </h3>
              <div className='bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto'>
                <div className='whitespace-pre-line text-sm text-gray-700 leading-relaxed'>
                  {transcript}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className='space-y-8'>
            {/* Performance Analysis and Optimal Solution - Side by Side */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
              {/* Performance Analysis */}
              <div className='bg-white rounded-lg shadow-lg p-6'>
                <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                  Performance Analysis
                </h3>
                <div className='space-y-4'>
                  {performanceData.categoryScores.map((category, index) => (
                    <div
                      key={index}
                      className='border-b border-gray-100 pb-4'
                    >
                      <div className='flex justify-between items-center mb-2'>
                        <span className='font-medium text-gray-900'>
                          {category.category}
                        </span>
                        <span className='text-sm font-semibold text-blue-600'>
                          {category.score}/100
                        </span>
                      </div>
                      <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                        <div
                          className='bg-blue-600 h-2 rounded-full transition-all duration-500'
                          style={{ width: `${category.score}%` }}
                        ></div>
                      </div>
                      <p className='text-sm text-gray-600'>
                        {category.feedback}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optimal Solution */}
              <div className='bg-white rounded-lg shadow-lg p-6'>
                <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                  Optimal Solution
                </h3>
                <div className='bg-gray-900 rounded-lg p-4 overflow-x-auto mb-4'>
                  <pre className='text-sm text-gray-100'>
                    <code>{solutionData.code}</code>
                  </pre>
                </div>
                <div className='grid grid-cols-1 gap-2 mb-4 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>Time Complexity:</span>
                    <span className='font-medium text-green-600'>
                      {solutionData.timeComplexity}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>Space Complexity:</span>
                    <span className='font-medium text-green-600'>
                      {solutionData.spaceComplexity}
                    </span>
                  </div>
                </div>
                {solutionData.explanation && (
                  <div className='p-4 bg-blue-50 rounded-lg'>
                    <h4 className='font-semibold text-blue-900 mb-2'>
                      Explanation:
                    </h4>
                    <p className='text-blue-800 text-sm'>
                      {solutionData.explanation}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Areas for Improvement */}
            <div className='bg-white rounded-lg shadow-lg p-6'>
              <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                Areas for Improvement
              </h3>
              <div className='space-y-4'>
                {improvementsData.map((improvement, index) => (
                  <div
                    key={index}
                    className='border-l-4 border-orange-400 pl-4 py-2'
                  >
                    <h4 className='font-semibold text-gray-900 mb-1'>
                      {improvement.category}
                    </h4>
                    <p className='text-gray-700 mb-2'>
                      {improvement.suggestion}
                    </p>
                    <p className='text-sm text-orange-600 font-medium'>
                      Impact: {improvement.impact}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Transcript Summary */}
            <div className='bg-white rounded-lg shadow-lg p-6'>
              <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                Interview Summary
              </h3>
              {analysisResult?.transcriptSummary?.keyMoments &&
              analysisResult.transcriptSummary.keyMoments.length > 0 ? (
                <>
                  <div className='space-y-4'>
                    {analysisResult.transcriptSummary.keyMoments.map(
                      (moment, index) => (
                        <div
                          key={index}
                          className='border-l-4 border-blue-400 pl-4 py-2'
                        >
                          <p className='text-gray-700 mb-1'>
                            {moment.description}
                          </p>
                          <p className='text-sm text-blue-600'>
                            {moment.significance}
                          </p>
                        </div>
                      )
                    )}
                  </div>

                  {/* Communication Patterns */}
                  {analysisResult?.transcriptSummary?.communicationPatterns && (
                    <div className='mt-6 pt-6 border-t border-gray-200'>
                      <h4 className='text-lg font-semibold text-gray-900 mb-4'>
                        Communication Analysis
                      </h4>
                      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-green-600'>
                            {
                              analysisResult.transcriptSummary
                                .communicationPatterns.clarity
                            }
                          </div>
                          <div className='text-sm text-gray-600'>Clarity</div>
                        </div>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-blue-600'>
                            {
                              analysisResult.transcriptSummary
                                .communicationPatterns.confidence
                            }
                          </div>
                          <div className='text-sm text-gray-600'>
                            Confidence
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-purple-600'>
                            {
                              analysisResult.transcriptSummary
                                .communicationPatterns.technical_accuracy
                            }
                          </div>
                          <div className='text-sm text-gray-600'>
                            Technical Accuracy
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-orange-600'>
                            {
                              analysisResult.transcriptSummary
                                .communicationPatterns.engagement
                            }
                          </div>
                          <div className='text-sm text-gray-600'>
                            Engagement
                          </div>
                        </div>
                      </div>

                      {/* Additional Communication Insights */}
                      {(analysisResult.transcriptSummary.communicationPatterns
                        .pace ||
                        analysisResult.transcriptSummary.communicationPatterns
                          .filler_words ||
                        analysisResult.transcriptSummary.communicationPatterns
                          .question_asking ||
                        analysisResult.transcriptSummary.communicationPatterns
                          .explanation_quality) && (
                        <div className='mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm'>
                          {analysisResult.transcriptSummary
                            .communicationPatterns.pace && (
                            <div className='bg-gray-50 rounded p-2 text-center'>
                              <div className='font-medium text-gray-700'>
                                Pace
                              </div>
                              <div className='text-gray-600 capitalize'>
                                {
                                  analysisResult.transcriptSummary
                                    .communicationPatterns.pace
                                }
                              </div>
                            </div>
                          )}
                          {analysisResult.transcriptSummary
                            .communicationPatterns.filler_words && (
                            <div className='bg-gray-50 rounded p-2 text-center'>
                              <div className='font-medium text-gray-700'>
                                Filler Words
                              </div>
                              <div className='text-gray-600 capitalize'>
                                {
                                  analysisResult.transcriptSummary
                                    .communicationPatterns.filler_words
                                }
                              </div>
                            </div>
                          )}
                          {analysisResult.transcriptSummary
                            .communicationPatterns.question_asking && (
                            <div className='bg-gray-50 rounded p-2 text-center'>
                              <div className='font-medium text-gray-700'>
                                Question Asking
                              </div>
                              <div className='text-gray-600 capitalize'>
                                {
                                  analysisResult.transcriptSummary
                                    .communicationPatterns.question_asking
                                }
                              </div>
                            </div>
                          )}
                          {analysisResult.transcriptSummary
                            .communicationPatterns.explanation_quality && (
                            <div className='bg-gray-50 rounded p-2 text-center'>
                              <div className='font-medium text-gray-700'>
                                Explanation Quality
                              </div>
                              <div className='text-gray-600 capitalize'>
                                {
                                  analysisResult.transcriptSummary
                                    .communicationPatterns.explanation_quality
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className='bg-gray-50 rounded-lg p-4'>
                  <p className='text-gray-600 text-sm'>
                    Transcript summary not available for this session.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className='flex justify-center space-x-4 mt-8'>
          <button
            onClick={() => {
              // Navigate to interview page with problem data in state
              navigate('/practice/coding_interview', {
                state: {
                  problem: {
                    title: sessionData.problemTitle,
                    description: sessionData.problemDescription,
                    difficulty: sessionData.difficulty,
                    company: sessionData.companyContext || '',
                    examples: sessionData.problemExamples,
                    constraints: sessionData.problemConstraints,
                  },
                },
              });
            }}
            className='px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors'
          >
            Practice Again
          </button>
          <button
            onClick={() => navigate('/practice')}
            className='px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors'
          >
            Return Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewResultPage;
